import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { path } from 'ramda';
import { Dispatch } from 'redux';
import { ActionType } from 'typesafe-actions';

import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { ISignSubjectInfo } from 'APIPackage/KnmModule/interfaces/ISignSubjectInfo';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IProfKind } from 'APIPackage/DictionariesModule/interfaces/IProfKind';
import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { knmCheckSignCertificate } from 'APIPackage/KnmModule/services/knmSignApi';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';

import { IKnmForm } from '../interfaces/IKnmForm';
import { knmSign, knmSignWithSaveSigner } from '../actions/knmActions';
import { KnmActionsEnum } from '../enums/KnmActionsEnum';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { useKnmFormSignHelpers } from './useKnmFormSignHelpers';
import { createCheckSignCertificateHandler } from '../common/createCheckSignSertificateHandler';

interface IUseKnmFormSignHandlersProps {
  knmData?: IKnm;
  settings: IAdminSetting[];
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  values: IKnmForm;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  closeSignModal: () => {
    type: KnmActionsEnum.KNM_REMOVE_VISIBLE_MODALS;
    payload: {
      value: KnmModalsEnum;
    };
  };
  knmSystemType: KnmSystemTypesEnum | undefined;
  pmTypesSelectOptions: IDictionarySelectOption<IProfKind>[] | undefined;
  isEnableKnoValidationSigning: boolean;
  handleCloseSignAfterCancelModal: () => {
    type: KnmActionsEnum.KNM_REMOVE_VISIBLE_MODALS;
    payload: {
      value: KnmModalsEnum;
    };
  };
}

export const useKnmFormSignHandlers = ({
  knmData,
  settings,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  values,
  knoOrganizationsSelectOptions,
  closeSignModal,
  knmSystemType,
  pmTypesSelectOptions,
  isEnableKnoValidationSigning,
  handleCloseSignAfterCancelModal,
}: IUseKnmFormSignHandlersProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    values.created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const { getSubjectInfo, checkErknmExtract } = useKnmFormSignHelpers({
    knmData,
    isKnmStatusFillInProcess,
    isKnmStatusReadyForApprove,
  });

  const checkSignCertificate = useCallback(
    createCheckSignCertificateHandler(dispatch, knmCheckSignCertificate, knmData?.id, isEnableKnoValidationSigning),
    [dispatch, knmData?.id, isEnableKnoValidationSigning],
  );

  const handleErpPmSign = (
    pmType: IDictionarySelectOption<IProfKind> | undefined,
    subjectInfo: ISignSubjectInfo,
    abortCheckCertificateCallback: () => void,
    knmDataId: number,
    certificate: string,
    actGuid?: string,
    prescriptionGuid?: string,
    isCancelSign?: boolean,
    onSuccessCheckCertificate?: (
      action?: ActionType<typeof knmSign> | ActionType<typeof knmSignWithSaveSigner>,
    ) => void,
    isSignAfterSuccessCheckCertificateAction?: boolean,
  ) => {
    let signer = knmData?.pmErknm?.inspectors?.find(
      i => i.fullName.toLowerCase().replaceAll(' ', '') === subjectInfo.fullname?.toLowerCase().replaceAll(' ', ''),
    );

    if (!signer && subjectInfo.givenname && subjectInfo.surename) {
      signer = knmData?.pmErknm?.inspectors?.find(
        i =>
          i.fullName.toLowerCase().replaceAll(' ', '') ===
          `${subjectInfo.givenname} ${subjectInfo.surename}`.toLowerCase().replaceAll(' ', ''),
      );
    }

    if (pmType?.code === ProfKindCodesEnum.PP) {
      checkSignCertificate(subjectInfo, abortCheckCertificateCallback, () => {
        if (isSignAfterSuccessCheckCertificateAction && onSuccessCheckCertificate) {
          onSuccessCheckCertificate(knmSign(certificate, knmDataId, signer, actGuid, prescriptionGuid, isCancelSign));
        } else if (!isSignAfterSuccessCheckCertificateAction && onSuccessCheckCertificate) {
          onSuccessCheckCertificate();
          dispatch(knmSign(certificate, knmDataId, signer, actGuid, prescriptionGuid, isCancelSign));
        } else {
          dispatch(knmSign(certificate, knmDataId, signer, actGuid, prescriptionGuid, isCancelSign));
        }
      });
    } else if (pmType?.code === ProfKindCodesEnum.OP) {
      checkSignCertificate(subjectInfo, abortCheckCertificateCallback, () => {
        if (isSignAfterSuccessCheckCertificateAction && onSuccessCheckCertificate) {
          onSuccessCheckCertificate(
            knmSignWithSaveSigner(certificate, knmDataId, signer, actGuid, prescriptionGuid, isCancelSign),
          );
        } else if (!isSignAfterSuccessCheckCertificateAction && onSuccessCheckCertificate) {
          onSuccessCheckCertificate();
          dispatch(knmSignWithSaveSigner(certificate, knmDataId, signer, actGuid, prescriptionGuid, isCancelSign));
        } else {
          dispatch(knmSignWithSaveSigner(certificate, knmDataId, signer, actGuid, prescriptionGuid, isCancelSign));
        }
      });
    }
  };

  // если будут различия в логике
  const handleErknmPmSign = handleErpPmSign;

  const handleSign = useCallback(
    async (
      certificate: string,
      actGuid?: string,
      prescriptionGuid?: string,
      abortCheckCertificate?: () => void,
      isCancelSign?: boolean,
      onSuccessCheckCertificate?: (
        action?: ActionType<typeof knmSign> | ActionType<typeof knmSignWithSaveSigner>,
      ) => void,
      isSignAfterSuccessCheckCertificateAction?: boolean,
      isNeedSubjectFioCheck?: boolean,
    ) => {
      if (knmData?.id) {
        const subjectName = (await path(['SubjectName'], certificate)) as string;

        const subjectInfo: ISignSubjectInfo = {
          ...getSubjectInfo(subjectName),
          cancelSignCheck: isCancelSign || isNeedSubjectFioCheck,
        };

        let signer = knmData?.pmErknm?.inspectors?.find(
          i => i.fullName.toLowerCase().replaceAll(' ', '') === subjectInfo.fullname?.toLowerCase().replaceAll(' ', ''),
        );

        if (!signer && subjectInfo.givenname && subjectInfo.surename) {
          signer = knmData?.pmErknm?.inspectors?.find(
            i =>
              i.fullName.toLowerCase().replaceAll(' ', '') ===
              `${subjectInfo.givenname} ${subjectInfo.surename}`.toLowerCase().replaceAll(' ', ''),
          );
        }

        if (
          isLaterThanAdminSetting(
            knmData?.created,
            settings,
            AdminSettingCodesEnum.KNM_VALIDATION_4_1_21_CERT_STARTS_FROM,
          )
        ) {
          const abortCheckCertificateCallback = () => {
            abortCheckCertificate && abortCheckCertificate();
            closeSignModal();
          };

          if (knmSystemType === KnmSystemTypesEnum.PM) {
            const pmType = pmTypesSelectOptions?.find(
              i =>
                i.value.dictId === knmData.pmErknm?.pmType?.dictId &&
                i.value.dictVersionId === knmData.pmErknm?.pmType?.dictVersionId,
            );

            if (knmData.version === KnmVersionsEnum.ERP) {
              handleErpPmSign(
                pmType,
                subjectInfo,
                abortCheckCertificateCallback,
                knmData.id,
                certificate,
                actGuid,
                prescriptionGuid,
                isCancelSign,
                onSuccessCheckCertificate,
                isSignAfterSuccessCheckCertificateAction,
              );
            } else if (knmData.version === KnmVersionsEnum.ERKNM) {
              handleErknmPmSign(
                pmType,
                subjectInfo,
                abortCheckCertificateCallback,
                knmData.id,
                certificate,
                actGuid,
                prescriptionGuid,
                isCancelSign,
                onSuccessCheckCertificate,
                isSignAfterSuccessCheckCertificateAction,
              );
            }
          } else if (knmSystemType === KnmSystemTypesEnum.ERKNM) {
            const onSuccessCheckCertificateCallback = () => {
              if (isSignAfterSuccessCheckCertificateAction && onSuccessCheckCertificate) {
                onSuccessCheckCertificate(
                  knmSign(certificate, knmData.id, signer, actGuid, prescriptionGuid, isCancelSign),
                );
              } else if (!isSignAfterSuccessCheckCertificateAction && onSuccessCheckCertificate) {
                onSuccessCheckCertificate();
                dispatch(knmSign(certificate, knmData.id, signer, actGuid, prescriptionGuid, isCancelSign));
              } else {
                dispatch(knmSign(certificate, knmData.id, signer, actGuid, prescriptionGuid, isCancelSign));
              }
            };

            if (actGuid || prescriptionGuid) {
              checkSignCertificate(subjectInfo, abortCheckCertificateCallback, onSuccessCheckCertificateCallback);
            } else if (isEnablePaperlessDocument) {
              checkSignCertificate(subjectInfo, abortCheckCertificateCallback, onSuccessCheckCertificateCallback);
            } else {
              checkErknmExtract(closeSignModal, () =>
                checkSignCertificate(subjectInfo, abortCheckCertificateCallback, onSuccessCheckCertificateCallback),
              );
            }
          } else {
            dispatch(knmSign(certificate, knmData.id, signer, actGuid, prescriptionGuid, isCancelSign));
          }
        } else {
          dispatch(knmSign(certificate, knmData.id, signer, actGuid, prescriptionGuid, isCancelSign));
        }
        closeSignModal();
      }
    },
    [closeSignModal, dispatch, knmData?.id, knmSystemType, pmTypesSelectOptions, knoOrganizationsSelectOptions],
  );

  const handleSignAfterCancelModal = useCallback(
    async (certificate: string, prescriptionGuid?: string) => {
      dispatch(coreSetLoading(true));
      await handleSign(certificate, undefined, prescriptionGuid, () => {}, true).finally(() => {
        dispatch(coreSetLoading(false));
        handleCloseSignAfterCancelModal();
      });
    },
    [handleSign, handleCloseSignAfterCancelModal],
  );

  return {
    handleSign,
    handleSignAfterCancelModal,
  };
};
